import { ArrowDownOutlined } from '@ant-design/icons';
import { makeStyles } from '@material-ui/styles';
import { Spin } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useContext } from 'react';
import { QUERY_GET_PROPERTY_TYPES } from 'common/queries/property';
import event from '~helpers/mixpanel';
import PropertyTypeItem from '~pages/onboarding/property-type/item';
import OnboardingContext from '../context';
import { pageType } from '~constants/pages';

const HEADER_HEIGHT = 96;
const FOOTER_HEIGHT = 100;

const useStyles = makeStyles(theme => ({
  wrap: {
    ...theme.flex.colCross,
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '136px 0',
    position: 'relative',

    '& h2': {
      ...theme.typography.h2,
      margin: '0 0 48px 0',
    },
  },
  scrollTip: {
    width: 566,
    height: 52,
    paddingTop: 16,
    textAlign: 'center',
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.colors.mainGreen6,
    position: 'fixed',
    bottom: 80,
    cursor: 'pointer',
    background: 'linear-gradient(360deg, #FFFFFF 51.55%, rgba(255, 255, 255, 0) 100%)',
  },
  arrowDown: {
    marginRight: '4px',
  },
}));

const PropertyType = ({ propertyLoading }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { propertyTypeSlug } = useContext(OnboardingContext);

  const [isScrollToBottom, setIsScrollToBottom] = useState(false);
  const { loading, error, data } = useQuery(QUERY_GET_PROPERTY_TYPES);

  const handleClickScrollDown = () => {
    window.scrollBy({
      top: window.innerHeight - HEADER_HEIGHT - FOOTER_HEIGHT,
      behavior: 'smooth',
    });
    event('Clicked OP Property Type Page', {
      'Clicked Position': 'Scroll Hint',
      'Property Type': propertyTypeSlug,
      'Current Page Type': pageType.onBoardingPage,
    });
  };

  const handleScrollPage = () => {
    const scrollTop = document?.documentElement?.scrollTop || document?.body?.scrollTop;
    const windowHeight = document?.documentElement?.clientHeight || document?.body?.clientHeight;
    const scrollHeight = document?.documentElement?.scrollHeight || document?.body?.scrollHeight;

    setIsScrollToBottom(scrollTop + windowHeight >= scrollHeight);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScrollPage);

    event('Viewed OP Property Type Page', { 'Current Page Type': pageType.onBoardingPage });

    return () => {
      window.removeEventListener('scroll', handleScrollPage);
    };
  }, []);

  return (
    <div className={classes.wrap}>
      <Choose>
        <When condition={loading || propertyLoading || error}>
          <Spin size="large" />
        </When>
        <Otherwise>
          <h2>{t('landlord.onboarding.property_type.title')}</h2>
          <For each="propertyType" of={data?.listPropertyTypes?.propertyTypes} index="index">
            <PropertyTypeItem key={index} propertyType={propertyType} />
          </For>
          <If condition={!isScrollToBottom}>
            <div className={classes.scrollTip} onClick={handleClickScrollDown}>
              <ArrowDownOutlined className={classes.arrowDown} />
              {t('landlord.onboarding.scroll.tip')}
            </div>
          </If>
        </Otherwise>
      </Choose>
    </div>
  );
};

PropertyType.propTypes = {
  propertyLoading: PropTypes.bool,
};

export default PropertyType;
